import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter } from '@fortawesome/pro-solid-svg-icons/faFilter'
import FiltersModal from './filtersModal'
import { IFilterOption, IFilterProps, IFilterRow } from 'interfaces/filterProps'
import { FilterInputsTypesEnum, FilterTypesInWords } from 'enum/filterType.enum'
import translationsTemplate from 'constants/translations'
import { es_dateFormat } from 'constants/date'
import { isObject, mergeDeep } from 'helpers/mergeDeep'
import { arrayGroupBy } from 'helpers/filter'
import styles from './index.module.scss'

export const Filters: React.FC<IFilterProps> = ({
  onlySimpleFilters = undefined,
  selectedFilters = undefined,
  columns = undefined,
  locale,
  loading = false,
  onChange,
  open = undefined,
  onVisibilityChange = undefined,
  customTrigger,
  dateFormat = es_dateFormat,
  clearOnChangeCondition = true,
  className = '',
}) => {
  const [showFilter, setShowFilter] = useState(false)
  const [translations, setTranslations] = useState(translationsTemplate.filters)
  const [selectedData, setSelectedData] = useState([] as any)
  const [generalCondition, setGeneralCondition] = useState('and')
  const [filterType, setFilterType] = useState(FilterTypesInWords.simple as string)
  const auxSelectedData = useRef([] as any)
  const [active, setActive] = useState(false)
  const { simple } = FilterTypesInWords

  useEffect(() => {
    auxSelectedData.current = selectedData
  }, [selectedData])

  useEffect(() => {
    open !== undefined && setShowFilter(open)
  }, [open])

  useEffect(() => {
    setActive(!!selectedFilters?.data?.length)
    setSelectedData(selectedFilters?.data || [])
    setFilterType(selectedFilters?.filterType || FilterTypesInWords.simple)
    setGeneralCondition(selectedFilters?.generalCondition || 'and')
  }, [selectedFilters])

  useEffect(() => {
    isObject(locale) && Object.keys(locale).length > 0 && setTranslations(mergeDeep(locale, translationsTemplate.filters))
  }, [locale])

  const handleChange = (data: any) => {
    const { index, input, type, values, group, childrensGroup } = data
    const aux = auxSelectedData.current.map((row: IFilterRow) => ({ ...row })) // Deepclone de bajo presupuesto
    if (!aux[index]) aux[index] = {}
    aux[index].group = group || 0
    aux[index][input] = values.value
    if (input !== FilterInputsTypesEnum.condition) {
      aux[index].selectedValueData = { ...aux[index].selectedValueData, ...values }
    }

    if (childrensGroup) {
      if (!aux[index].childrensGroup) aux[index] = { ...aux[index], childrensGroup: {} }
      aux[index].childrensGroup.group = childrensGroup.group || 0
      aux[index].childrensGroup[childrensGroup.input] = childrensGroup.values.value
      if (input !== FilterInputsTypesEnum.condition) {
        aux[index].childrensGroup.selectedValueData = { ...aux[index].childrensGroup.selectedValueData, ...childrensGroup.values }
      }
    }

    if (input === FilterInputsTypesEnum.input) {
      aux[index].type = type

      if (clearOnChangeCondition) {
        delete aux[index][FilterInputsTypesEnum.operator]
        delete aux[index][FilterInputsTypesEnum.compareTo]
        delete aux[index][FilterInputsTypesEnum.predicate]
        delete aux[index][FilterInputsTypesEnum.optionalId]
        delete aux[index][FilterInputsTypesEnum.async]
        delete aux[index][FilterInputsTypesEnum.additionalFields]
      }

      if (values.predicate) {
        aux[index][FilterInputsTypesEnum.predicate] = values.predicate
      }

      if (values.optionalId) {
        aux[index][FilterInputsTypesEnum.optionalId] = values.optionalId
      }

      if (values?.async) {
        aux[index].async = values?.async
      }

      if (values?.additionalFields) {
        aux[index].additionalFields = values?.additionalFields
      }
    }

    if (input === FilterInputsTypesEnum.operator) {
      if (values.mapBy) {
        aux[index][FilterInputsTypesEnum.mapBy] = values.mapBy
      } else {
        delete aux[index][FilterInputsTypesEnum.mapBy]
      }

      if (values.value.includes('null') || type === 'boolean') {
        aux[index][FilterInputsTypesEnum.compareTo] = true
      } else {
        if (clearOnChangeCondition) {
          delete aux[index][FilterInputsTypesEnum.compareTo]
          if (aux[index].childrensGroup) {
            delete aux[index].childrensGroup
          }
        }
      }
    }

    if (input === FilterInputsTypesEnum.compareTo) {
      if (clearOnChangeCondition && aux[index].childrensGroup && !childrensGroup) {
        delete aux[index].childrensGroup
      }
    }

    if (childrensGroup) {
      if (childrensGroup.input === FilterInputsTypesEnum.input) {
        aux[index].childrensGroup.type = childrensGroup.type
        if (clearOnChangeCondition) {
          delete aux[index].childrensGroup[FilterInputsTypesEnum.operator]
          delete aux[index].childrensGroup[FilterInputsTypesEnum.compareTo]
          delete aux[index].childrensGroup[FilterInputsTypesEnum.predicate]
          delete aux[index].childrensGroup[FilterInputsTypesEnum.optionalId]
          delete aux[index].childrensGroup[FilterInputsTypesEnum.async]
          delete aux[index].childrensGroup[FilterInputsTypesEnum.additionalFields]
        }

        if (childrensGroup.values?.predicate) {
          aux[index][FilterInputsTypesEnum.predicate] = values.predicate
        }

        if (childrensGroup.values?.optionalId) {
          aux[index][FilterInputsTypesEnum.optionalId] = values.optionalId
        }

        if (childrensGroup.values?.async) {
          aux[index].childrensGroup.async = childrensGroup.values?.async
        }

        if (childrensGroup.values?.additionalFields) {
          aux[index].childrensGroup.additionalFields = childrensGroup.values?.additionalFields
        }
      }

      if (childrensGroup.input === FilterInputsTypesEnum.operator) {
        if (values.mapBy) {
          aux[index][FilterInputsTypesEnum.mapBy] = values.mapBy
        } else {
          delete aux[index][FilterInputsTypesEnum.mapBy]
        }

        if (childrensGroup.values.value.includes('null') || type === 'boolean') {
          aux[index].childrensGroup[FilterInputsTypesEnum.compareTo] = true
        } else {
          if (clearOnChangeCondition) {
            delete aux[index].childrensGroup[FilterInputsTypesEnum.compareTo]
          }
        }
      }
    }
    setSelectedData(aux)
  }

  const handleDelete = (index: number) => {
    const aux = selectedData.map((row: IFilterRow) => ({ ...row })) // Deepclone de bajo presupuesto
    aux.splice(index, 1)

    const grouped = arrayGroupBy(aux, 'group')
    const rows = [] as any
    grouped.map((row: any) => {
      row.data.map((data: any) => {
        const condition = row.data.find(({ group }: any) => group === data.group)
        rows.push({ ...data, condition: filterType === simple ? 'and' : condition.condition || 'and' })
      })
    })

    setSelectedData(rows)
  }

  const handleAddFilterRow = (group = 0) => {
    const aux = selectedData.map((row: IFilterRow) => ({ ...row })) // Deepclone de bajo presupuesto
    setSelectedData(aux.concat(!aux.length ? [{ group: 0 }, { group }] : [{ group }]))
  }

  const handleSubmit = () => {
    const result = selectedData
      .filter(({ compareTo, type, childrensGroup }: any) => (!!compareTo && type !== 'group') || (type === 'group' && !!compareTo && !!childrensGroup))
      .sort(({ group: groupA }: any, { group: groupB }: any) => groupA - groupB)
    const grouped = arrayGroupBy(result, 'group')
    const rows = [] as any
    grouped.map((row: any) => {
      row.data.map((data: any) => {
        const condition = row.data.find(({ group }: any) => group === data.group)
        rows.push({ ...data, condition: filterType === simple ? 'and' : condition.condition || 'and' })
      })
    })
    onChange({ data: rows, generalCondition, filterType, groupedData: grouped })
    setShowFilter(false)
    onVisibilityChange && onVisibilityChange(false)
  }

  const handleCloseModal = () => {
    setShowFilter(false)
    onVisibilityChange && onVisibilityChange(false)
  }

  return (
    <div className={`${styles.container} rankmiTable-filters`}>
      {!customTrigger && (
        <Button
          className={`${styles.filterButton} ${active ? styles.active : ''} rankmiTable-filters-button`}
          data-test="rankmi-table-filters-trigger"
          loading={loading}
          onClick={() => {
            setShowFilter(true)
            onVisibilityChange && onVisibilityChange(true)
          }}
        >
          {!loading && <FontAwesomeIcon className="mr-2" icon={faFilter} />} {translations.filter}
        </Button>
      )}
      {showFilter && (
        <FiltersModal
          className={className}
          columnsData={columns as IFilterOption[]}
          dateFormat={dateFormat}
          generalCondition={generalCondition}
          loading={loading}
          onlySimpleFilters={onlySimpleFilters}
          selectedData={selectedData}
          setFilterType={setFilterType}
          show={showFilter}
          translations={translations}
          type={filterType}
          onAddFilterRow={handleAddFilterRow}
          onChange={handleChange}
          onClose={handleCloseModal}
          onDelete={handleDelete}
          onGeneralConditionChange={setGeneralCondition}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  )
}
